import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { status } from 'constants/general';
import { fetchAddOrEditAdvertSubmission } from 'modules/adverts/actions';
import { fetchSearchProducts, resetSearchProducts } from 'modules/common/actions';
import { campaignTypeEnum } from 'modules/common/enums';
import AddAdvertComponent from 'modules/adverts/components/AddAdvertComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const AddAdvertContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { campaignInfo, staticData } = useSelector(state => ({
    campaignInfo: state.campaigns.campaignInfo,
    staticData: state.common.staticData,
  }));

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const campaignKey = searchParams.get('campaignKey');
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [shouldShowLandingField, setShouldShowLandingField] = useState(false);
  const [shouldShowImageField, setShouldShowImageField] = useState(false);
  const [shouldShowProductField, setShouldShowProductField] = useState(false);
  const [formFieldsValues, setFormFieldsValues] = useState({
    name: '',
    zone: [],
    landingType: '',
    landingValue: '',
    imageData: null,
    products: [],
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    name: true,
    zone: true,
    landingType: true,
    landingValue: true,
    imageData: true,
    products: true,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    if (campaignInfo.status === status.SUCCESS) {
      const campaignTypeId = campaignInfo.details?.campaignTypeDto?.campaignTypeId;

      setShouldShowLandingField(campaignTypeId === campaignTypeEnum.bannerImage);
      setShouldShowImageField(campaignTypeId === campaignTypeEnum.bannerImage);
      setShouldShowProductField(campaignTypeId === campaignTypeEnum.search);

      setFormFieldsValues(prevValue => {
        const fieldsObj = { ...prevValue };
        fieldsObj.landingValue = (campaignTypeId !== campaignTypeEnum.bannerImage && 'value') || '';
        fieldsObj.imageData =
          (campaignTypeId !== campaignTypeEnum.bannerImage && { absoluteRatio: 2.05 }) || null;
        fieldsObj.products = (campaignTypeId !== campaignTypeEnum.search && ['value']) || [];

        return fieldsObj;
      });
    }
  }, [campaignInfo]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const addOrEditAdvertSuccessCallback = () => {
    navigate(`/adverts?campaignKey=${campaignKey}`);
  };

  const getSearchProductsResults = query => {
    const body = {
      data: {
        query,
        from: 1,
        size: 5,
        isTrending: false,
        Version: 2,
      },
    };

    dispatch(fetchSearchProducts(body));
  };

  const resetSearchProductsResults = () => {
    dispatch(resetSearchProducts());
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextInputFieldChange = (fieldName, fieldEvent) => {
    const { value } = fieldEvent.target;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;

      return fieldsObj;
    });
  };

  const handleDropdownFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = fieldValue.key;
      fieldsObj.landingValue = '';
      return fieldsObj;
    });
  };

  const handleMultiSelectionDropdownFieldChange = (fieldName, fieldValue) => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = fieldValue;

      return fieldsObj;
    });
  };

  const handleUploadFileChange = fileData => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj.imageData = fileData;

      return fieldsObj;
    });
  };

  const handleProductItemClick = (product, searchProductItemClickFunc) => {
    dispatch(resetSearchProducts());
    searchProductItemClickFunc();

    if (formFieldsValues.products.some(item => item.productKey === product.productKey) === false)
      setFormFieldsValues(prevValue => {
        const updatedProducts = [...prevValue.products];
        updatedProducts.push(product);

        return {
          ...prevValue,
          products: updatedProducts,
        };
      });
  };

  const handleProductItemDeleteIconClick = product => {
    const updatedProducts = formFieldsValues.products.filter(
      result => result.productKey !== product.productKey,
    );

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj.products = updatedProducts;

      return fieldsObj;
    });
  };

  const handleAddOrEditAdvertBtnClick = () => {
    let isFormValid = true;

    if (
      !formFieldsValues.name ||
      !formFieldsValues.zone.length ||
      !formFieldsValues.landingValue ||
      !formFieldsValues.imageData ||
      formFieldsValues.imageData?.absoluteRatio !== 2.05 ||
      formFieldsValues.products.length === 0
    ) {
      setFormFieldsValidation({
        name: !!formFieldsValues.name,
        zone: !!formFieldsValues.zone.length,
        landingValue: !!formFieldsValues.landingValue,
        imageData:
          !!formFieldsValues.imageData && formFieldsValues.imageData?.absoluteRatio === 2.05,
        products: formFieldsValues.products.length !== 0,
      });

      isFormValid = false;
    }

    if (isFormValid === false) return;

    setFormFieldsValidation({
      name: true,
      landingType: true,
      landingValue: true,
      zone: true,
      imageData: true,
      products: true,
    });

    const body = {
      data: {
        campaignKey,
        adItemName: formFieldsValues.name,
        adItemType: campaignInfo.details.campaignTypeDto.campaignTypeName,
        zoneKeys: formFieldsValues.zone.map(zone => zone.key),
      },
      imageData: formFieldsValues.imageData.fileData,
      addOrEditAdvertCallbackActionSuccess: addOrEditAdvertSuccessCallback,
    };

    const campaignTypeId = campaignInfo.details?.campaignTypeDto?.campaignTypeId;
    if (campaignTypeId === campaignTypeEnum.bannerImage) {
      body.data.clickUrl = formFieldsValues.landingValue;

      const selectedLandingPage = staticData.details?.data?.landingPages?.find(
        landingPage => landingPage.landingPage === formFieldsValues.landingType,
      );
      if (selectedLandingPage?.adLandingPageId)
        body.data.AdLandingPageId = selectedLandingPage.adLandingPageId;
    }
    if (campaignTypeId === campaignTypeEnum.search)
      body.data.adItemContent = formFieldsValues.products;

    dispatch(fetchAddOrEditAdvertSubmission(body));
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <AddAdvertComponent
      shouldShowLandingField={shouldShowLandingField}
      shouldShowImageField={shouldShowImageField}
      shouldShowProductField={shouldShowProductField}
      formFieldsValues={formFieldsValues}
      formFieldsValidation={formFieldsValidation}
      getSearchProductsResults={getSearchProductsResults}
      resetSearchProductsResults={resetSearchProductsResults}
      handleTextInputFieldChange={handleTextInputFieldChange}
      handleDropdownFieldChange={handleDropdownFieldChange}
      handleMultiSelectionDropdownFieldChange={handleMultiSelectionDropdownFieldChange}
      handleUploadFileChange={handleUploadFileChange}
      handleProductItemClick={handleProductItemClick}
      handleProductItemDeleteIconClick={handleProductItemDeleteIconClick}
      handleAddOrEditAdvertBtnClick={handleAddOrEditAdvertBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

AddAdvertContainer.propTypes = {};

AddAdvertContainer.defaultProps = {};

export default AddAdvertContainer;
